import React, {
    useCallback,
    useEffect,
    useMemo,
} from "react";

import {
    FiAlertCircle,
    FiFileText,
    FiRefreshCw,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchGstr3bReport,

    gstr3bSection31Selector,
    gstr3bSection4Selector,
    gstr3bTaxPositionSelector,
    gstr3bSummarySelector,
    gstr3bMetaSelector,
    gstr3bWarningsSelector,

    gstReportLoadingSelector,
    gstReportErrorSelector,
} from "../../../../../../../store/billing/gst/gstReportSlice";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        toNumber(value)
    );


const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};


/*
|--------------------------------------------------------------------------
| TAX ROW COMPONENT
|--------------------------------------------------------------------------
*/

const TaxRow = ({
    label,
    data = {},
}) => {
    return (
        <tr>

            <td>
                <strong>
                    {label}
                </strong>
            </td>

            <td className="text-right">
                {formatCurrency(
                    data?.taxableValue
                )}
            </td>

            <td className="text-right">
                {formatCurrency(
                    data?.igst
                )}
            </td>

            <td className="text-right">
                {formatCurrency(
                    data?.cgst
                )}
            </td>

            <td className="text-right">
                {formatCurrency(
                    data?.sgst
                )}
            </td>

            <td className="text-right">
                {formatCurrency(
                    data?.cess
                )}
            </td>

            <td className="text-right">
                <strong>
                    {formatCurrency(
                        data?.totalTax
                    )}
                </strong>
            </td>

        </tr>
    );
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const Gstr3bReport = ({
    fromDate,
    toDate,
    financialYear,
}) => {
    const dispatch =
        useDispatch();


    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const section31 =
        useSelector(
            gstr3bSection31Selector
        ) || {};

    const section4 =
        useSelector(
            gstr3bSection4Selector
        ) || {};

    const taxPosition =
        useSelector(
            gstr3bTaxPositionSelector
        ) || {};

    const summary =
        useSelector(
            gstr3bSummarySelector
        ) || {};

    const meta =
        useSelector(
            gstr3bMetaSelector
        ) || {};

    const warnings =
        useSelector(
            gstr3bWarningsSelector
        ) || [];

    const loading =
        useSelector(
            gstReportLoadingSelector
        ) || {};

    const errors =
        useSelector(
            gstReportErrorSelector
        ) || {};


    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadData =
        useCallback(
            async () => {
                await dispatch(
                    fetchGstr3bReport({
                        fromDate,
                        toDate,
                        financialYear,
                    })
                );
            },
            [
                dispatch,
                fromDate,
                toDate,
                financialYear,
            ]
        );


    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | DATA
    |--------------------------------------------------------------------------
    */

    const outwardTaxable =
        section31?.outwardTaxable ||
        {};

    const zeroRated =
        section31?.zeroRated ||
        {};

    const nilExempt =
        section31?.nilExempt ||
        {};

    const nonGst =
        section31?.nonGst ||
        {};

    const reverseCharge =
        section31?.reverseCharge ||
        {};


    const eligibleItc =
        section4?.eligibleItc ||
        {};

    const normalItc =
        section4?.normalItc ||
        {};

    const reverseChargeItc =
        section4?.reverseChargeItc ||
        {};

    const reversedItc =
        section4?.reversedItc ||
        {};

    const ineligibleItc =
        section4?.ineligibleItc ||
        {};


    /*
    |--------------------------------------------------------------------------
    | CARDS
    |--------------------------------------------------------------------------
    */

    const cards =
        useMemo(
            () => [
                {
                    label:
                        "Output GST",

                    value:
                        formatCurrency(
                            taxPosition
                                ?.output
                                ?.total
                        ),

                    help:
                        "Tax on outward supplies",
                },

                {
                    label:
                        "RCM Liability",

                    value:
                        formatCurrency(
                            taxPosition
                                ?.reverseCharge
                                ?.total
                        ),

                    help:
                        "Tax payable under reverse charge",
                },

                {
                    label:
                        "Available ITC",

                    value:
                        formatCurrency(
                            taxPosition
                                ?.availableItc
                        ),

                    help:
                        "Eligible purchase input tax",
                },

                {
                    label:
                        "Estimated Net Tax",

                    value:
                        formatCurrency(
                            taxPosition
                                ?.estimatedNetTax
                        ),

                    help:
                        "Management estimate",
                },
            ],
            [
                taxPosition,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="gstr3b-report">

            {/* =====================================================
                ERROR
            ===================================================== */}

            {errors.gstr3b && (

                <div className="gst-dashboard-alert">

                    <FiAlertCircle />

                    <span>
                        {errors.gstr3b}
                    </span>

                </div>

            )}


            {/* =====================================================
                WARNINGS
            ===================================================== */}

            {warnings.map(
                (
                    warning,
                    index
                ) => (

                    <div
                        key={
                            index
                        }
                        className="gstr3b-warning"
                    >

                        <FiAlertCircle />

                        <span>
                            {warning}
                        </span>

                    </div>

                )
            )}


            {/* =====================================================
                SUMMARY CARDS
            ===================================================== */}

            <section className="gst-dashboard-summary-grid">

                {cards.map(
                    (
                        card
                    ) => (

                        <div
                            key={
                                card.label
                            }
                            className="gst-dashboard-summary-card"
                        >

                            <span>
                                {card.label}
                            </span>

                            <strong>
                                {card.value}
                            </strong>

                            <small>
                                {card.help}
                            </small>

                        </div>

                    )
                )}

            </section>


            {/* =====================================================
                DOCUMENT SUMMARY
            ===================================================== */}

            <section className="gstr3b-document-summary">

                <div>

                    <span>
                        Sales Invoices
                    </span>

                    <strong>
                        {toNumber(
                            summary
                                ?.documents
                                ?.salesInvoices
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Purchase Bills
                    </span>

                    <strong>
                        {toNumber(
                            summary
                                ?.documents
                                ?.purchaseBills
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Credit Notes
                    </span>

                    <strong>
                        {toNumber(
                            summary
                                ?.documents
                                ?.creditNotes
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Debit Notes
                    </span>

                    <strong>
                        {toNumber(
                            summary
                                ?.documents
                                ?.debitNotes
                        )}
                    </strong>

                </div>

            </section>


            {/* =====================================================
                3.1 OUTWARD SUPPLIES
            ===================================================== */}

            <section className="gstr3b-section">

                <div className="gstr3b-section-header">

                    <div>

                        <FiFileText />

                        <div>

                            <strong>
                                3.1 Outward Supplies
                            </strong>

                            <span>
                                Taxable outward supplies and reverse charge
                            </span>

                        </div>

                    </div>


                    <button
                        type="button"
                        className="gst-dashboard-button"
                        disabled={
                            loading.gstr3b
                        }
                        onClick={
                            loadData
                        }
                    >

                        <FiRefreshCw
                            className={
                                loading.gstr3b
                                    ? "gst-dashboard-spinner"
                                    : ""
                            }
                        />

                        Refresh

                    </button>

                </div>


                <div className="gstr3b-table-wrapper">

                    <table className="gstr3b-table">

                        <thead>

                            <tr>

                                <th>
                                    Nature of Supplies
                                </th>

                                <th className="text-right">
                                    Taxable Value
                                </th>

                                <th className="text-right">
                                    IGST
                                </th>

                                <th className="text-right">
                                    CGST
                                </th>

                                <th className="text-right">
                                    SGST
                                </th>

                                <th className="text-right">
                                    Cess
                                </th>

                                <th className="text-right">
                                    Total Tax
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            <TaxRow
                                label="Taxable outward supplies"
                                data={
                                    outwardTaxable
                                }
                            />

                            <TaxRow
                                label="Zero-rated outward supplies"
                                data={
                                    zeroRated
                                }
                            />

                            <TaxRow
                                label="Nil rated / exempt supplies"
                                data={
                                    nilExempt
                                }
                            />

                            <TaxRow
                                label="Non-GST outward supplies"
                                data={
                                    nonGst
                                }
                            />

                            <TaxRow
                                label="Inward supplies liable to reverse charge"
                                data={
                                    reverseCharge
                                }
                            />

                        </tbody>

                    </table>

                </div>

            </section>


            {/* =====================================================
                SECTION 4 ITC
            ===================================================== */}

            <section className="gstr3b-section">

                <div className="gstr3b-section-header">

                    <div>

                        <FiFileText />

                        <div>

                            <strong>
                                4. Eligible ITC
                            </strong>

                            <span>
                                Input tax credit from purchase transactions
                            </span>

                        </div>

                    </div>

                </div>


                <div className="gstr3b-table-wrapper">

                    <table className="gstr3b-table">

                        <thead>

                            <tr>

                                <th>
                                    ITC Category
                                </th>

                                <th className="text-right">
                                    Taxable Value
                                </th>

                                <th className="text-right">
                                    IGST
                                </th>

                                <th className="text-right">
                                    CGST
                                </th>

                                <th className="text-right">
                                    SGST
                                </th>

                                <th className="text-right">
                                    Cess
                                </th>

                                <th className="text-right">
                                    Total ITC
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            <TaxRow
                                label="Normal purchase ITC"
                                data={
                                    normalItc
                                }
                            />

                            <TaxRow
                                label="RCM ITC"
                                data={
                                    reverseChargeItc
                                }
                            />

                            <TaxRow
                                label="Total eligible ITC"
                                data={
                                    eligibleItc
                                }
                            />

                            <TaxRow
                                label="ITC reversed"
                                data={
                                    reversedItc
                                }
                            />

                            <TaxRow
                                label="Ineligible ITC"
                                data={
                                    ineligibleItc
                                }
                            />

                        </tbody>

                    </table>

                </div>

            </section>


            {/* =====================================================
                TAX POSITION
            ===================================================== */}

            <section className="gstr3b-tax-position">

                <div className="gstr3b-tax-position-card">

                    <span>
                        Output Tax
                    </span>

                    <strong>
                        {formatCurrency(
                            taxPosition
                                ?.output
                                ?.total
                        )}
                    </strong>

                    <small>
                        Outward supply tax
                    </small>

                </div>


                <div className="gstr3b-tax-position-card">

                    <span>
                        RCM Liability
                    </span>

                    <strong>
                        {formatCurrency(
                            taxPosition
                                ?.reverseCharge
                                ?.total
                        )}
                    </strong>

                    <small>
                        Reverse charge payable
                    </small>

                </div>


                <div className="gstr3b-tax-position-card">

                    <span>
                        Gross Liability
                    </span>

                    <strong>
                        {formatCurrency(
                            taxPosition
                                ?.grossLiability
                        )}
                    </strong>

                    <small>
                        Output + RCM
                    </small>

                </div>


                <div className="gstr3b-tax-position-card">

                    <span>
                        Available ITC
                    </span>

                    <strong>
                        {formatCurrency(
                            taxPosition
                                ?.availableItc
                        )}
                    </strong>

                    <small>
                        Eligible input credit
                    </small>

                </div>


                <div className="gstr3b-tax-position-card">

                    <span>
                        Estimated Net Tax
                    </span>

                    <strong>
                        {formatCurrency(
                            taxPosition
                                ?.estimatedNetTax
                        )}
                    </strong>

                    <small>
                        Before statutory ITC utilisation logic
                    </small>

                </div>


                <div className="gstr3b-tax-position-card">

                    <span>
                        Excess ITC
                    </span>

                    <strong>
                        {formatCurrency(
                            taxPosition
                                ?.excessItc
                        )}
                    </strong>

                    <small>
                        ITC above gross liability
                    </small>

                </div>

            </section>


            {/* =====================================================
                COMPONENT-WISE POSITION
            ===================================================== */}

            <section className="gstr3b-section">

                <div className="gstr3b-section-header">

                    <div>

                        <FiFileText />

                        <div>

                            <strong>
                                Component-wise Tax Position
                            </strong>

                            <span>
                                IGST, CGST, SGST and cess overview
                            </span>

                        </div>

                    </div>

                </div>


                <div className="gstr3b-component-grid">

                    <div>

                        <span>
                            Output IGST
                        </span>

                        <strong>
                            {formatCurrency(
                                taxPosition
                                    ?.output
                                    ?.igst
                            )}
                        </strong>

                    </div>


                    <div>

                        <span>
                            Output CGST
                        </span>

                        <strong>
                            {formatCurrency(
                                taxPosition
                                    ?.output
                                    ?.cgst
                            )}
                        </strong>

                    </div>


                    <div>

                        <span>
                            Output SGST
                        </span>

                        <strong>
                            {formatCurrency(
                                taxPosition
                                    ?.output
                                    ?.sgst
                            )}
                        </strong>

                    </div>


                    <div>

                        <span>
                            ITC IGST
                        </span>

                        <strong>
                            {formatCurrency(
                                taxPosition
                                    ?.eligibleItc
                                    ?.igst
                            )}
                        </strong>

                    </div>


                    <div>

                        <span>
                            ITC CGST
                        </span>

                        <strong>
                            {formatCurrency(
                                taxPosition
                                    ?.eligibleItc
                                    ?.cgst
                            )}
                        </strong>

                    </div>


                    <div>

                        <span>
                            ITC SGST
                        </span>

                        <strong>
                            {formatCurrency(
                                taxPosition
                                    ?.eligibleItc
                                    ?.sgst
                            )}
                        </strong>

                    </div>

                </div>

            </section>


            {/* =====================================================
                META
            ===================================================== */}

            <section className="gstr3b-meta">

                <div>

                    <span>
                        From
                    </span>

                    <strong>
                        {formatDate(
                            meta?.fromDate
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        To
                    </span>

                    <strong>
                        {formatDate(
                            meta?.toDate
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Financial Year
                    </span>

                    <strong>
                        {meta?.financialYear ||
                            "-"}
                    </strong>

                </div>


                <div>

                    <span>
                        Generated
                    </span>

                    <strong>
                        {formatDate(
                            meta?.generatedAt
                        )}
                    </strong>

                </div>

            </section>

        </div>
    );
};


export default Gstr3bReport;